import { DialogActions, DialogContent, Stack } from '@mui/material'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import { useMessageSource } from 'src/i18n/useMessageSource'
import {
  CATEGORY_ID_PREFIX,
  ClassificationCategoryAndScores,
} from 'src/screens/factsheets/factsheet/FactsheetEffectivenessClassificationUtils'
import { ExpandableEffectivenessClassificationSelectionCard } from 'src/screens/shared/common/filter-card/filters/effectiveness-classifications/ExpandableEffectivenessClassificationSelectionCard'
import { PrimaryButton, SecondaryButton } from 'src/shared/button/Buttons'
import { EffectivenessClassificationFilterItem } from 'src/shared/constants/filter-constants'
import { FEATURE_OPERATOR, FEATURE_OPERATOR_TYPE } from 'src/shared/constants/reporting-constants'
import { Option } from 'src/shared/form/control'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { CheckboxData } from 'src/shared/form/control/CheckboxGroupField'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import styled from 'styled-components'

interface Props {
  categories: ClassificationCategoryAndScores[]
  onCancel: () => void
  onConfirm: (values: EffectivenessClassificationFilterItem[], operator: FEATURE_OPERATOR_TYPE) => void
  selectedEffectivenessClassifications: EffectivenessClassificationFilterItem[]
  selectedOperator: FEATURE_OPERATOR_TYPE
}

interface FormValues {
  operator: FEATURE_OPERATOR_TYPE
  scores: Record<string, number[]>
}

const AutoCompleteFieldStyled = styled(AutoCompleteField)`
  margin-bottom: ${({ theme }) => theme.spacing(1)};

  .MuiFormHelperText-root {
    display: none;
  }
`

export const AddEffectivenessClassificationsModalDialog = ({
  categories,
  onCancel,
  onConfirm,
  selectedEffectivenessClassifications,
  selectedOperator,
}: Props): ReactElement => {
  const { getMessage } = useMessageSource()
  const [initialFormValues, setInitialFormValues] = useState<FormValues>({
    operator: FEATURE_OPERATOR.AND,
    scores: {},
  })

  useEffect(() => {
    const selectedScoresObject: Record<string, number[]> = {}

    selectedEffectivenessClassifications.forEach((item) => {
      const categoryKey = `${CATEGORY_ID_PREFIX}${item.categoryId}`

      if (!selectedScoresObject[categoryKey]) {
        selectedScoresObject[categoryKey] = [item.scoreId]
      } else {
        selectedScoresObject[categoryKey].push(item.scoreId)
      }
    })

    setInitialFormValues({
      operator: selectedOperator,
      scores: selectedScoresObject,
    })
  }, [selectedEffectivenessClassifications, selectedOperator])

  const classificationOperatorOptions: Option[] = useMemo(
    () => [
      {
        label: getMessage('label.operator.AND').toUpperCase(),
        value: FEATURE_OPERATOR.AND,
      },
      {
        label: getMessage('label.operator.OR').toUpperCase(),
        value: FEATURE_OPERATOR.OR,
      },
    ],
    [getMessage],
  )

  const adaptSelectedEffectivenessClassifications = (
    scores: Record<string, number[]>,
  ): EffectivenessClassificationFilterItem[] => {
    return Object.entries(scores).flatMap(([categoryKey, scoreIds]) =>
      scoreIds.map((scoreId) => ({
        categoryId: parseInt(categoryKey.replace(CATEGORY_ID_PREFIX, ''), 10),
        scoreId,
      })),
    )
  }

  const getCheckboxData = (scores: ClassificationCategoryAndScores['scores']): CheckboxData[] =>
    scores.map((score) => ({
      label: score.label,
      value: score.value,
    }))

  const onSubmit = () => {
    document
      .getElementById('effectiveness-classification-selection-form')
      ?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const handleSubmitLocal = async (values: FormValues): Promise<void> => {
    onConfirm(adaptSelectedEffectivenessClassifications(values.scores), values.operator)
  }

  return (
    <>
      <DialogContent>
        <Form<FormValues>
          initialValues={initialFormValues}
          onSubmit={handleSubmitLocal}
          render={({ handleSubmit, values }) => {
            return (
              <form id="effectiveness-classification-selection-form" onSubmit={handleSubmit}>
                <Stack spacing={1}>
                  <AutoCompleteFieldStyled
                    options={classificationOperatorOptions}
                    name="operator"
                    label={getMessage('label.reporting.feature.connection.operator')}
                    disableClearable
                  />
                  {categories.map((category) => (
                    <ExpandableEffectivenessClassificationSelectionCard
                      key={category.categoryId}
                      categoryName={category.categoryName}
                      name={`scores.${CATEGORY_ID_PREFIX}${category.categoryId}`}
                      checkboxData={getCheckboxData(category.scores)}
                      showSelectionWithinGroupIcon={
                        !!values.scores[`${CATEGORY_ID_PREFIX}${category.categoryId}`]?.length
                      }
                    />
                  ))}
                </Stack>
                <DirtyFormSpy />
              </form>
            )
          }}
        />
      </DialogContent>
      <DialogActions>
        <SecondaryButton messageKey="button.cancel" onClick={onCancel} />
        <PrimaryButton messageKey="button.add" type="submit" onClick={onSubmit} />
      </DialogActions>
    </>
  )
}
